import { entitiesClient, type SearchKind } from "@entities/entitiesClient";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@utils/use-debounced-value";

export const entitiesKeys = {
  all: ["entity-search"] as const,
  search: (q: string, kinds: readonly SearchKind[]) =>
    ["entity-search", q, kinds] as const,
};

export const MIN_QUERY_LENGTH = 2;

const useEntitySearch = (q: string, kinds: readonly SearchKind[]) =>
  useQuery({
    queryKey: entitiesKeys.search(q, kinds),
    queryFn: () => entitiesClient.search(q, kinds),
    enabled: q.length > 0,
    staleTime: 30_000,
  });

export const useEntityTypeahead = (
  raw: string,
  kinds: readonly SearchKind[],
) => {
  const trimmed = raw.trim();
  const debounced = useDebouncedValue(trimmed, 250);
  // Gate on the live query too: right after a reset `debounced` would drive a stale list.
  const ready =
    trimmed.length >= MIN_QUERY_LENGTH && debounced.length >= MIN_QUERY_LENGTH;
  const search = useEntitySearch(ready ? debounced : "", kinds);
  return { ...search, ready, trimmed };
};
